import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';

const generateRandomData = (min: number, max: number, weight: number, points = 24) => {
  return Array.from({ length: points }, () =>
    (Math.random() * weight * (max - min) + min).toFixed(2)
  );
};

// Fonction pour générer des données avec une anomalie temporaire
const generateDataWithAnomaly = (
  min: number,
  max: number,
  weight: number,
  points = 24,
  anomalyOptions?: {
    startPoint?: number;
    duration?: number;
    severity?: number; // 0-1, où 1 est une baisse de 100%
  }
) => {
  const {
    startPoint = Math.floor(Math.random() * (points - 5)) + 2, // Commence l'anomalie à un point aléatoire
    duration = Math.floor(Math.random() * 3) + 2, // Durée de l'anomalie entre 2 et 4 points
    severity = Math.random() * 0.7 + 0.3, // Sévérité entre 30% et 100%
  } = anomalyOptions || {};

  return Array.from({ length: points }, (_, i) => {
    // Si nous sommes dans la période d'anomalie
    if (i >= startPoint && i < startPoint + duration) {
      // Réduire la valeur en fonction de la sévérité
      return ((Math.random() * weight * (max - min) + min) * (1 - severity)).toFixed(2);
    }
    // Sinon, générer une valeur normale
    return (Math.random() * weight * (max - min) + min).toFixed(2);
  });
};

interface Data {
  downloadSpeed: SpeedData[];
  downloadSpeedMonth: SpeedData[];
  uploadSpeed: SpeedData[];
  uploadSpeedMonth: SpeedData[];
}

interface SpeedData {
  name: string;
  type: string;
  smooth: boolean;
  data: string[];
}

const QoSPerformanceDashboard = () => {
  const [data, setData] = useState<Data>();
  const providers = [
    { name: 'Filecoin', weight: 0.9, color: '#FF5733' },
    { name: 'StorJ', weight: 1.1, color: '#D9AD93' },
    { name: 'AWS S3', weight: 2.3, color: '#EAE8E6' },
    { name: 'GCS', weight: 2.4, color: '#778DFF' },
    //{ name: 'Azure', weight: 5 },
  ];

  useEffect(() => {
    // Generate mock data with anomalies
    const newData: Data = {
      downloadSpeed: providers.map((provider, index) => ({
        name: provider.name,
        type: 'line',
        smooth: false,
        // Ajouter une anomalie uniquement pour AWS S3
        data:
          provider.name === 'AWS S3'
            ? generateDataWithAnomaly(
                3 * provider.weight,
                3.5 * provider.weight,
                provider.weight,
                24,
                {
                  startPoint: 8, // Anomalie à 8h
                  duration: 3, // Durée de 3 heures
                  severity: 0.85, // Baisse de 85%
                }
              )
            : generateRandomData(3 * provider.weight, 3.5 * provider.weight, provider.weight),
      })),
      downloadSpeedMonth: providers.map((provider) => ({
        name: provider.name,
        type: 'line',
        smooth: false,
        data: generateRandomData(3 * provider.weight, 3.5 * provider.weight, provider.weight, 30),
      })),
      uploadSpeed: providers.map((provider) => ({
        name: provider.name,
        type: 'line',
        smooth: false,
        // Ajouter une anomalie pour Filecoin
        data:
          provider.name === 'Filecoin'
            ? generateDataWithAnomaly(
                3 * provider.weight,
                3.5 * provider.weight,
                provider.weight,
                24,
                {
                  startPoint: 15, // Anomalie à 15h
                  duration: 4, // Durée de 4 heures
                  severity: 0.7, // Baisse de 70%
                }
              )
            : generateRandomData(3 * provider.weight, 3.5 * provider.weight, provider.weight),
      })),
      uploadSpeedMonth: providers.map((provider) => ({
        name: provider.name,
        type: 'line',
        smooth: false,
        // Ajouter une anomalie pour GCS dans les données mensuelles
        data:
          provider.name === 'GCS'
            ? generateDataWithAnomaly(
                3 * provider.weight,
                3.5 * provider.weight,
                provider.weight,
                30,
                {
                  startPoint: 20, // Anomalie au jour 20
                  duration: 3, // Durée de 3 jours
                  severity: 0.6, // Baisse de 60%
                }
              )
            : generateRandomData(3 * provider.weight, 3.5 * provider.weight, provider.weight, 30),
      })),
    };
    setData(newData);
  }, []);

  return (
    <div className="h-full overflow-y-auto pb-6 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-gray-900">Performance Analytics</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Download Speed Chart */}
          <ReactECharts
            option={{
              color: providers.map((provider) => provider.color),
              title: {
                text: '24h Download Speed (Mbps)',
                textStyle: { color: '#000', fontWeight: 'bold', fontSize: 16 },
              },
              tooltip: { trigger: 'axis' },
              legend: { data: providers, right: '10%', textStyle: { color: '#333' } },
              xAxis: {
                type: 'category',
                data: Array.from({ length: 24 }, (_, i) => `${i}h`),
                axisLabel: { color: '#aaa' },
              },
              yAxis: { type: 'value', axisLabel: { color: '#aaa' } },
              series: data?.downloadSpeed || [],
            }}
            style={{ height: 300 }}
          />

          {/* Download Speed Chart Days*/}
          <ReactECharts
            option={{
              color: providers.map((provider) => provider.color),
              title: {
                text: '30d Download Speed (Mbps)',
                textStyle: { color: '#000', fontWeight: 'bold', fontSize: 16 },
              },
              tooltip: { trigger: 'axis' },
              legend: { data: providers, right: '10%', textStyle: { color: '#333' } },
              xAxis: {
                type: 'category',
                data: Array.from({ length: 30 }, (_, i) => `${i}d`),
                axisLabel: { color: '#aaa' },
              },
              yAxis: { type: 'value', axisLabel: { color: '#aaa' } },
              series: data?.downloadSpeedMonth || [],
            }}
            style={{ height: 300 }}
          />

          {/* Upload Speed Chart */}
          <ReactECharts
            option={{
              color: providers.map((provider) => provider.color),
              title: {
                text: '24h Upload Speed (Mbps)',
                textStyle: { color: '#000', fontWeight: 'bold', fontSize: 16 },
              },
              tooltip: { trigger: 'axis' },
              legend: { data: providers, right: '10%', textStyle: { color: '#333' } },
              xAxis: {
                type: 'category',
                data: Array.from({ length: 24 }, (_, i) => `${i}h`),
                axisLabel: { color: '#aaa' },
              },
              yAxis: { type: 'value', axisLabel: { color: '#aaa' } },
              series: data?.uploadSpeed || [],
            }}
            style={{ height: 300 }}
          />

          {/* Upload Speed Chart */}
          <ReactECharts
            option={{
              color: providers.map((provider) => provider.color),
              title: {
                text: '30d Upload Speed (Mbps)',
                textStyle: { color: '#000', fontWeight: 'bold', fontSize: 16 },
              },
              tooltip: { trigger: 'axis' },
              legend: { data: providers, right: '10%', textStyle: { color: '#333' } },
              xAxis: {
                type: 'category',
                data: Array.from({ length: 30 }, (_, i) => `${i}d`),
                axisLabel: { color: '#aaa' },
              },
              yAxis: { type: 'value', axisLabel: { color: '#aaa' } },
              series: data?.uploadSpeedMonth || [],
            }}
            style={{ height: 300 }}
          />

          <ReactECharts
            option={{
              color: providers.map((provider) => provider.color),
              title: {
                text: 'Total Data Transferred (GB)',
                textStyle: { color: '#000', fontWeight: 'bold', fontSize: 16 },
              },
              tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
              legend: { data: providers, textStyle: { color: '#333' }, top: '10%' },
              xAxis: {
                type: 'category',
                data: Array.from({ length: 24 }, (_, i) => `${i}h`),
                axisLabel: { color: '#aaa' },
              },
              yAxis: { type: 'value', axisLabel: { color: '#aaa' } },
              series: providers.map((provider) => ({
                name: provider.name,
                type: 'line',
                areaStyle: {}, // This makes it an area chart
                stack: 'total',
                smooth: false,
                data:
                  provider.name === 'StorJ'
                    ? generateDataWithAnomaly(100, 1000, 1, 24, {
                        startPoint: 10,
                        duration: 3,
                        severity: 0.75,
                      })
                    : generateRandomData(100, 1000, 1),
              })),
            }}
            style={{ height: 300 }}
          />
        </div>
      </div>
    </div>
  );
};

export default QoSPerformanceDashboard;
